import React from 'react';
import Button from './Button';
import CacheGroupDisplay from './CacheGroupDisplay';
import { getCacheStatus, clearCacheByKeyPattern, getCacheKeys, cacheManager, getCacheablePatterns } from '../utils/cache-manager';
import { 
  getKVCacheInfo, 
  clearKVCache, 
  formatBytes as formatBytesKV, 
  formatRelativeTime, 
  calculateCacheEfficiency,
  kvCacheMonitor,
  KVCacheUIHelpers 
} from '../utils/route-tool-getkvcache';
import { useTranslation } from '../context/TranslationContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDatabase, 
  faTrashAlt, 
  faClock, 
  faCheckCircle, 
  faExclamationTriangle,
  faInfoCircle,
  faServer,
  faSync,
  faChartBar,
  faFolder,
  faLanguage,
  faGlobe,
  faRobot,
  faCog,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';

/**
 * Page d'administration pour gérer et surveiller le cache mémoire local
 */
const CacheAdmin = () => {
  const { t, loadPageTranslations } = useTranslation();
  const [cacheStatus, setCacheStatus] = React.useState({});
  const [kvCacheInfo, setKvCacheInfo] = React.useState(null);
  const [lastUpdate, setLastUpdate] = React.useState(new Date());
  const [clearingRoutes, setClearingRoutes] = React.useState(new Set());
  const [loadingKVCache, setLoadingKVCache] = React.useState(false);
  const [clearingKVCache, setClearingKVCache] = React.useState(false);
  const [entriesLimit, setEntriesLimit] = React.useState(20); // Nombre d'entrées à afficher

  // Charger les traductions au montage du composant
  React.useEffect(() => {
    loadPageTranslations('ADMIN_CACHE');
  }, [loadPageTranslations]);

  // Charger le statut du cache au montage du composant
  React.useEffect(() => {
    updateCacheStatus();
  }, []);

  // Fonction pour mettre à jour le statut du cache
  const updateCacheStatus = React.useCallback(() => {
    const newStatus = getCacheStatus();
    setCacheStatus(newStatus);
    setLastUpdate(new Date());
  }, []);

  // Charger les informations sur les caches KV
  React.useEffect(() => {
    const loadKVCacheInfo = async () => {
      setLoadingKVCache(true);
      try {
        const kvInfo = await getKVCacheInfo();
        setKvCacheInfo(kvInfo);
      } catch (error) {
        debug.error(config, 'Erreur lors du chargement des informations KV:', error);
        setKvCacheInfo(null);
      } finally {
        setLoadingKVCache(false);
      }
    };

    loadKVCacheInfo();

    // Configurer la surveillance automatique des caches KV
    const handleKVCacheUpdate = (data, error) => {
      if (error) {
        debug.error(config, 'Erreur de surveillance KV Cache:', error);
      } else {
        setKvCacheInfo(data);
      }
    };

    kvCacheMonitor.addListener(handleKVCacheUpdate);
    kvCacheMonitor.start();

    return () => {
      kvCacheMonitor.removeListener(handleKVCacheUpdate);
      kvCacheMonitor.stop();
    };
  }, []);

  /**
   * Charge plus d'entrées dans la liste du cache
   */
  const handleLoadMoreEntries = React.useCallback(() => {
    setEntriesLimit(prevLimit => {
      // Limiter à 100 entrées maximum pour éviter les problèmes de performances
      const maxLimit = 100;
      const newLimit = prevLimit + 20;
      return Math.min(newLimit, maxLimit);
    });
  }, []);

  /**
   * Vide les entrées contenant un pattern spécifique
   */
  const handleClearByPattern = React.useCallback(async (pattern) => {
    setClearingRoutes(prev => new Set([...prev, pattern]));
    
    try {
      const deletedCount = clearCacheByKeyPattern(pattern);
      debug.log(config, `Cache vidé pour le pattern ${pattern}: ${deletedCount} entrées supprimées`);
    } catch (error) {
      debug.error(config, `Erreur lors du vidage du cache pour ${pattern}:`, error);
    } finally {
      // Retirer le pattern de la liste des vidages en cours après un délai
      setTimeout(() => {
        setClearingRoutes(prev => {
          const newSet = new Set(prev);
          newSet.delete(pattern);
          return newSet;
        });
        
        // Mettre à jour l'état du cache après avoir vidé un pattern
        updateCacheStatus();
      }, 1000);
    }
  }, [updateCacheStatus]);

  /**
   * Actualise manuellement les informations sur les caches KV
   */
  const handleRefreshKVCache = React.useCallback(async () => {
    setLoadingKVCache(true);
    try {
      const kvInfo = await kvCacheMonitor.update();
      setKvCacheInfo(kvInfo);
      debug.log(config, 'Informations KV Cache actualisées');
    } catch (error) {
      debug.error(config, 'Erreur lors de l\'actualisation des informations KV:', error);
    } finally {
      setLoadingKVCache(false);
    }
  }, []);

  /**
   * Vide les caches KV
   */
  const handleClearKVCache = React.useCallback(async (namespace = null) => {
    setClearingKVCache(true);
    
    try {
      const options = namespace ? { namespace } : {};
      debug.log(config, `Nettoyage des caches KV...`, options);
      const result = await clearKVCache(options);
      debug.log(config, `Cache KV vidé: ${result.deletedCount} entrées supprimées`);
      
      // Actualiser les informations après le nettoyage
      await handleRefreshKVCache();
    } catch (error) {
      debug.error(config, 'Erreur lors du vidage du cache KV:', error);
    } finally {
      setClearingKVCache(false);
    }
  }, [handleRefreshKVCache]);
  
  /**
   * Vide tout le cache
   */
  const handleClearAllCache = React.useCallback(async () => {
    setClearingRoutes(prev => new Set([...prev, 'all']));
    
    try {
      const deletedCount = cacheManager.clearAll();
      debug.log(config, `Tout le cache vidé: ${deletedCount} entrées supprimées`);
      setEntriesLimit(20); // Réinitialiser la limite d'affichage
      updateCacheStatus(); // Mettre à jour l'affichage du cache
    } catch (error) {
      debug.error(config, 'Erreur lors du vidage complet du cache:', error);
    } finally {
      setTimeout(() => {
        setClearingRoutes(prev => {
          const newSet = new Set(prev);
          newSet.delete('all');
          return newSet;
        });
      }, 1000);
    }
  }, [updateCacheStatus]);

  /**
   * Regroupe les entrées du cache par les 3 premiers niveaux de la clé
   * Ex: "config/read/languages?lang=FR" -> groupe "config/read/languages"
   */
  const groupCacheEntries = React.useCallback((entries) => {
    const groups = {};
    
    entries.forEach(entry => {
      // Enlever les paramètres de requête (tout après ?)
      const keyWithoutParams = entry.key.split('?')[0];
      
      // Extraire les 3 premiers niveaux de la clé
      const parts = keyWithoutParams.split('/');
      const groupKey = parts.slice(0, 3).join('/');
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(entry);
    });
    
    return groups;
  }, []);

  /**
   * Obtient l'icône appropriée selon le type de groupe
   */
  const getGroupIcon = React.useCallback((groupName) => {
    if (groupName.includes('language') || groupName.includes('translation')) return faLanguage;
    if (groupName.includes('ia') || groupName.includes('model')) return faRobot;
    if (groupName.includes('config')) return faCog;
    if (groupName.includes('wa')) return faGlobe;
    return faFolder;
  }, []);

  /**
   * Obtient la classe de couleur selon le type de groupe
   */
  const getGroupColorStyle = React.useCallback((groupName) => {
    if (groupName.includes('language') || groupName.includes('translation')) return { backgroundColor: 'var(--color-success-light)', color: 'var(--color-success)' };
    if (groupName.includes('ia') || groupName.includes('model')) return { backgroundColor: 'var(--color-info-light)', color: 'var(--color-info)' };
    if (groupName.includes('config')) return { backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' };
    if (groupName.includes('wa')) return { backgroundColor: 'var(--color-accent-light)', color: 'var(--color-accent)' };
    return { backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)' };
  }, []);

  /**
   * Formate la taille en octets de manière lisible
   */
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Formate une durée en millisecondes de manière lisible
   */
  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    if (ms < 3600000) return `${Math.round(ms / 60000)}min`;
    return `${Math.round(ms / 3600000)}h`;
  };

  /**
   * Composant pour afficher les actions de nettoyage rapide
   */
  const QuickClearActions = React.useCallback(() => {
    const patterns = ['ui_languages', 'ui_translations', 'ia_models', 'manuscript_tags'];
    
    return (
      <div className="flex flex-wrap gap-2">
        {patterns.map(pattern => (
          <Button
            key={pattern}
            variant="warning"
            size="sm"
            loading={clearingRoutes.has(pattern)}
            onClick={() => handleClearByPattern(pattern)}
            title={t('ADMIN_CACHE', 'CLEAR_PATTERN_TITLE', 'Vider le cache pour {{pattern}}', { pattern })}
          >
            <FontAwesomeIcon icon={faTrashAlt} className="mr-1" />
            {pattern}
          </Button>
        ))}
      </div>
    );
  }, [clearingRoutes, handleClearByPattern, t]);

  /**
   * Composant pour afficher les actions de nettoyage rapide pour le cache KV
   */
  const QuickClearKVActions = React.useCallback(() => {
    // Utiliser les namespaces dynamiquement à partir des données du cache KV
    const namespaces = kvCacheInfo && kvCacheInfo.namespaces ? 
      Object.keys(kvCacheInfo.namespaces) : [];
    
    // Filtrer pour éviter les namespaces trop longs ou non pertinents
    const filteredNamespaces = namespaces.filter(namespace => 
      namespace && 
      namespace.length < 50 && 
      namespace !== 'default'
    );
    
    return (
      <div className="flex flex-wrap gap-2">
        {filteredNamespaces.length > 0 ? (
          filteredNamespaces.map(namespace => (
            <Button
              key={namespace}
              variant="warning"
              size="sm"
              loading={clearingKVCache}
              onClick={() => handleClearKVCache(namespace)}
              title={t('ADMIN_CACHE', 'CLEAR_KV_NAMESPACE_TITLE', 'Vider le cache KV pour {{namespace}}', { namespace })}
            >
              <FontAwesomeIcon icon={faTrashAlt} className="mr-1" />
              {namespace.length > 20 ? `${namespace.substring(0, 18)}...` : namespace}
            </Button>
          ))
        ) : (
          <div className="text-gray-500 text-sm italic">
            {t('ADMIN_CACHE', 'NO_NAMESPACES', 'Aucun namespace disponible')}
          </div>
        )}
      </div>
    );
  }, [kvCacheInfo, clearingKVCache, handleClearKVCache, t]);

  /**
   * Composant pour afficher les informations sur les caches KV
   */
  const KVCacheSection = React.useCallback(() => {
    if (loadingKVCache && !kvCacheInfo) {
      return (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center space-x-2 mb-4">
            <FontAwesomeIcon icon={faServer} className="text-blue-600" />
            <h2 className="text-lg font-semibold">{t('ADMIN_CACHE', 'KV_CACHE_LOADING', 'Cache KV - Chargement...')}</h2>
          </div>
          <div className="text-center py-8">
            <FontAwesomeIcon icon={faSync} className="text-3xl text-gray-400 animate-spin" />
            <p className="text-gray-500 mt-2">{t('ADMIN_CACHE', 'KV_CACHE_FETCHING', 'Récupération des informations sur les caches KV...')}</p>
          </div>
        </div>
      );
    }

    if (!kvCacheInfo) {
      return (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon={faServer} className="text-red-600" />
              <h2 className="text-lg font-semibold">{t('ADMIN_CACHE', 'KV_CACHE_ERROR', 'Cache KV - Erreur')}</h2>
            </div>
            <Button
              variant="primary"
              size="sm"
              loading={loadingKVCache}
              onClick={handleRefreshKVCache}
              title={t('ADMIN_CACHE', 'RETRY', 'Réessayer')}
            >
              <FontAwesomeIcon icon={faSync} className="mr-2" />
              {t('ADMIN_CACHE', 'RETRY', 'Réessayer')}
            </Button>
          </div>
          <div className="text-center py-8 text-gray-500">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-3xl mb-2" />
            <p>{t('ADMIN_CACHE', 'KV_CACHE_FETCH_ERROR', 'Impossible de récupérer les informations sur les caches KV')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <FontAwesomeIcon icon={faServer} className="text-blue-600 mr-3" />
          {t('ADMIN_CACHE', 'KV_CACHE_WORKER_INFO', 'Cache KV Worker - Informations')}
        </h1>

        {/* Statistiques KV globales */}
        <h2 className="text-lg font-semibold mb-4">
          {t('ADMIN_CACHE', 'KV_GLOBAL_STATS', 'Statistiques Globales KV')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="stat-card" style={{'--_sc-bg': 'var(--color-primary-light)', '--_sc-fg': 'var(--color-primary)'}}>
            <div className="stat-card-value">{kvCacheInfo.totalKeys}</div>
            <div className="stat-card-label">{t('ADMIN_CACHE', 'TOTAL_KV_KEYS', 'Clés KV totales')}</div>
          </div>
          <div className="stat-card" style={{'--_sc-bg': 'var(--color-success-light)', '--_sc-fg': 'var(--color-success)'}}>
            <div className="stat-card-value">{formatBytesKV(kvCacheInfo.totalSize)}</div>
            <div className="stat-card-label">{t('ADMIN_CACHE', 'TOTAL_SIZE', 'Taille totale')}</div>
          </div>
          <div className="stat-card" style={{'--_sc-bg': 'var(--color-info-light)', '--_sc-fg': 'var(--color-info)'}}>
            <div className="stat-card-value">{Object.keys(kvCacheInfo.namespaces || {}).length}</div>
            <div className="stat-card-label">{t('ADMIN_CACHE', 'NAMESPACES', 'Namespaces')}</div>
          </div>
          <div className="stat-card" style={{'--_sc-bg': 'var(--color-accent-light)', '--_sc-fg': 'var(--color-accent)'}}>
            <div className="stat-card-value">
              {kvCacheInfo.statistics && kvCacheInfo.statistics.hitRatio ? Math.round(kvCacheInfo.statistics.hitRatio) : 0}%
            </div>
            <div className="stat-card-label">{t('ADMIN_CACHE', 'SUCCESS_RATE', 'Taux de succès')}</div>
          </div>
        </div>
        
        {kvCacheInfo.statistics && kvCacheInfo.statistics.hits > 0 && (
          <div className="mt-4 mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-700">
              <strong>{t('ADMIN_CACHE', 'KV_HIT_RATIO', 'Taux de succès du cache KV :')}</strong> {' '}
              {Math.round(kvCacheInfo.statistics.hitRatio)}% ({kvCacheInfo.statistics.hits} hits / {kvCacheInfo.statistics.misses} misses)
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between mt-4 mb-6">
          <p className="text-sm text-gray-500">
            {kvCacheInfo.configuration && kvCacheInfo.configuration.timestamp && 
             t('ADMIN_CACHE', 'LAST_KV_UPDATE', 'Dernière mise à jour : {{time}}', { 
               time: new Date(kvCacheInfo.configuration.timestamp).toLocaleTimeString() 
             })
            }
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRefreshKVCache}
            title={t('ADMIN_CACHE', 'REFRESH_KV_INFO', 'Actualiser les informations du cache KV')}
          >
            <FontAwesomeIcon icon={faSync} className={loadingKVCache ? 'animate-spin' : ''} />
          </Button>
        </div>
        
        {/* Actions rapides KV */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              {t('ADMIN_CACHE', 'KV_QUICK_ACTIONS', 'Actions Rapides KV')}
            </h3>
            <Button
              variant="danger"
              size="sm"
              loading={clearingKVCache}
              onClick={() => handleClearKVCache()}
              title={t('ADMIN_CACHE', 'CLEAR_ALL_KV_CACHE', 'Vider tout le cache KV')}
            >
              <FontAwesomeIcon icon={faTrashAlt} className="mr-2" />
              {t('ADMIN_CACHE', 'CLEAR_KV', 'Vider tout KV')}
            </Button>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-900 mb-2">
              {t('ADMIN_CACHE', 'CLEAR_BY_NAMESPACE', 'Nettoyer par namespace')}
            </h4>
            <QuickClearKVActions />
          </div>
        </div>

        {/* Namespaces KV */}
        {Object.keys(kvCacheInfo.namespaces || {}).length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4">{t('ADMIN_CACHE', 'KV_NAMESPACES', 'Namespaces KV')}</h2>
            <div className="space-y-3">
              {Object.entries(kvCacheInfo.namespaces).map(([namespace, namespaceData]) => {
                const efficiency = calculateCacheEfficiency(namespaceData.keyCount, namespaceData.keyCount);
                const colorClass = KVCacheUIHelpers.getNamespaceColor(namespace);
                const iconName = KVCacheUIHelpers.getNamespaceIcon(namespace);
                
                // Mapping des noms d'icônes vers les icônes FontAwesome
                const iconMap = {
                  faLanguage, faGlobe, faRobot, faCog, faDatabase, faServer, faUser, faFolder
                };
                const icon = iconMap[iconName] || faFolder;

                return (
                  <div key={namespace} className="wa-form-group wa-form-group-compact mb-6">
                    <label className="wa-label">
                      <FontAwesomeIcon icon={icon} className="mr-2" />
                      <span className={`px-2 py-1 rounded-full text-xs ${colorClass} mr-2`}>
                        {namespace}
                      </span>
                      {t('ADMIN_CACHE', 'KEYS_COUNT', '{{count}} clés', { count: namespaceData.keyCount })}
                    </label>
                    <div className="flex items-center justify-between w-full">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="flex items-center">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-1" />
                            {t('ADMIN_CACHE', 'ENTRIES_COUNT', '{{count}} entrées', { count: namespaceData.keyCount })}
                          </span>
                          <span className="text-gray-500">
                            {formatBytesKV(namespaceData.totalSize)}
                          </span>
                          {namespaceData.lastAccessed && (
                            <span className="text-gray-400 text-xs">
                              <FontAwesomeIcon icon={faClock} className="mr-1" />
                              {formatRelativeTime(namespaceData.lastAccessed)}
                            </span>
                          )}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${Math.min(efficiency, 100)}%` }}
                          />
                        </div>
                      </div>
                      <Button
                        variant="warning"
                        size="sm"
                        loading={clearingKVCache}
                        className="ml-4 flex-shrink-0"
                        onClick={() => handleClearKVCache(namespace)}
                        title={t('ADMIN_CACHE', 'CLEAR_NAMESPACE', 'Vider le namespace {{namespace}}', { namespace })}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </Button>
                    </div>
                    
                    {/* Liste des clés individuelles */}
                    {namespaceData.keys && namespaceData.keys.length > 0 && (
                      <div className="mt-3 pl-6 pr-2">
                        <div className="text-xs font-medium text-gray-600 mb-2">
                          {t('ADMIN_CACHE', 'INDIVIDUAL_KEYS', 'Clés individuelles')}:
                        </div>
                        <div className="overflow-y-auto max-h-40 bg-gray-50 rounded-lg p-2 text-xs font-mono">
                          {namespaceData.keys.map((keyInfo, idx) => (
                            <div key={idx} className="py-1 px-2 hover:bg-gray-100 rounded flex justify-between items-center">
                              <div className="truncate mr-2 flex-grow">
                                {keyInfo.key}
                              </div>
                              <div className="text-gray-500 flex-shrink-0 whitespace-nowrap">
                                {formatBytesKV(keyInfo.size)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Configuration KV */}
        {kvCacheInfo.configuration && (
          <div className="mt-2">
            <h2 className="text-lg font-semibold mb-4">{t('ADMIN_CACHE', 'KV_CONFIGURATION', 'Configuration KV')}</h2>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 space-y-2">
                <p className="flex items-center">
                  <FontAwesomeIcon icon={faServer} className="text-blue-500 mr-2" />
                  <strong>{t('ADMIN_CACHE', 'ENVIRONMENT', 'Environnement')}:</strong> 
                  <span className="ml-2">{kvCacheInfo.configuration.environment}</span>
                </p>
                <p className="flex items-center">
                  <FontAwesomeIcon icon={faDatabase} className="text-purple-500 mr-2" />
                  <strong>{t('ADMIN_CACHE', 'KV_STORE', 'KV Store')}:</strong>
                  <span className="ml-2">{kvCacheInfo.configuration.kvStoreName}</span>
                </p>
                <p className="flex items-center">
                  <FontAwesomeIcon icon={faClock} className="text-green-500 mr-2" />
                  <strong>{t('ADMIN_CACHE', 'DEFAULT_TTL', 'TTL par défaut')}:</strong>
                  <span className="ml-2">{t('ADMIN_CACHE', 'DAYS_COUNT', '{{count}} jours', { count: Math.round(kvCacheInfo.configuration.defaultTTL / 86400) })}</span>
                </p>
                <p className="flex items-center">
                  <FontAwesomeIcon icon={faSync} className="text-cyan-500 mr-2" />
                  <strong>{t('ADMIN_CACHE', 'LAST_UPDATE', 'Dernière mise à jour')}:</strong>
                  <span className="ml-2">{formatRelativeTime(kvCacheInfo.configuration.timestamp)}</span>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [kvCacheInfo, loadingKVCache, clearingKVCache, handleRefreshKVCache, handleClearKVCache, t]);  return (
    <div className="p-6 space-y-6">
    <div className="bg-white p-6 rounded-lg shadow">
      <h1 className="text-2xl font-bold text-gray-900">
        {t('ADMIN_CACHE', 'PAGE_TITLE', 'Cache Mémoire - Administration')}
      </h1>

      {/* Statistiques globales */}
        <h2 className="text-lg font-semibold mb-4">
          {t('ADMIN_CACHE', 'GLOBAL_STATS', 'Statistiques Globales')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stat-card" style={{'--_sc-bg': 'var(--color-primary-light)', '--_sc-fg': 'var(--color-primary)'}}>
            <div className="stat-card-value">{cacheStatus.totalEntries || 0}</div>
            <div className="stat-card-label">{t('ADMIN_CACHE', 'TOTAL_ENTRIES', 'Entrées totales')}</div>
          </div>
          <div className="stat-card" style={{'--_sc-bg': 'var(--color-success-light)', '--_sc-fg': 'var(--color-success)'}}>
            <div className="stat-card-value">{cacheStatus.stats?.hits || 0}</div>
            <div className="stat-card-label">{t('ADMIN_CACHE', 'CACHE_HITS', 'Hits cache')}</div>
          </div>
          <div className="stat-card" style={{'--_sc-bg': 'var(--color-warning-light)', '--_sc-fg': 'var(--color-warning)'}}>
            <div className="stat-card-value">{cacheStatus.stats?.misses || 0}</div>
            <div className="stat-card-label">{t('ADMIN_CACHE', 'CACHE_MISSES', 'Misses cache')}</div>
          </div>
          <div className="stat-card" style={{'--_sc-bg': 'var(--color-info-light)', '--_sc-fg': 'var(--color-info)'}}>
            <div className="stat-card-value">{cacheStatus.loadingRequests || 0}</div>
            <div className="stat-card-label">{t('ADMIN_CACHE', 'LOADING_REQUESTS', 'Requêtes en cours')}</div>
          </div>
        </div>
        
        {cacheStatus.stats && (cacheStatus.stats.hits + cacheStatus.stats.misses) > 0 && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-700">
              <strong>{t('ADMIN_CACHE', 'HIT_RATIO', 'Taux de succès du cache :')}</strong> {' '}
              {Math.round((cacheStatus.stats.hits / (cacheStatus.stats.hits + cacheStatus.stats.misses)) * 100)}%
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500">
            {t('ADMIN_CACHE', 'LAST_UPDATE', 'Dernière mise à jour : {{time}}', { time: lastUpdate.toLocaleTimeString() })}
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={updateCacheStatus}
            title={t('ADMIN_CACHE', 'REFRESH_CACHE_INFO', 'Actualiser les informations du cache')}
          >
            <FontAwesomeIcon icon={faSync} />
          </Button>
        </div>

      {/* Actions de nettoyage rapide */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            {t('ADMIN_CACHE', 'QUICK_ACTIONS', 'Actions Rapides')}
          </h2>
          <Button
            variant="danger"
            loading={clearingRoutes.has('all')}
            onClick={handleClearAllCache}
            size="sm"
            title={t('ADMIN_CACHE', 'CLEAR_ALL_TITLE', 'Vider tout le cache')}
          >
            <FontAwesomeIcon icon={faTrashAlt} className="mr-2" />
            {t('ADMIN_CACHE', 'CLEAR_ALL', 'Vider tout')}
          </Button>
        </div>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">
              {t('ADMIN_CACHE', 'CLEAR_BY_PATTERN', 'Nettoyer par type de données')}
            </h3>
            <QuickClearActions />
          </div>
        </div>
      </div>

      {/* Chemins mis en cache */}
       <h2 className="text-lg font-semibold mb-4 my-6">
          {t('ADMIN_CACHE', 'CACHED_PATHS', 'Chemins mis en cache')}
        </h2>
        <div className="space-y-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('ADMIN_CACHE', 'PATH_PATTERN', 'Pattern de chemin')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getCacheablePatterns().map((pattern, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-blue-600">
                      {pattern}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-700 flex items-center">
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mr-2" />
              {t('ADMIN_CACHE', 'CACHE_PATH_INFO', 'Les requêtes GET vers ces chemins sont automatiquement mises en cache.')}
            </div>
          </div>
        </div>


      {/* Détails des entrées */}
       <h2 className="text-lg font-semibold mb-4 my-6">
          {t('ADMIN_CACHE', 'CACHE_ENTRIES', 'Détail des Entrées en Cache')}
        </h2>
        
        {cacheStatus.entries && cacheStatus.entries.length > 0 ? (
          <div className="space-y-4">
            {(() => {
              // Limiter les entrées affichées selon entriesLimit
              const limitedEntries = cacheStatus.entries.slice(0, entriesLimit);
              // Regrouper les entrées par les 3 premiers niveaux
              const groups = groupCacheEntries(limitedEntries);
              // Trier les groupes par nom
              const sortedGroupNames = Object.keys(groups).sort();
              
              return sortedGroupNames.map((groupName) => (
                <CacheGroupDisplay
                  key={groupName}
                  groupName={groupName}
                  entries={groups[groupName]}
                  formatBytes={formatBytes}
                  formatDuration={formatDuration}
                  onClear={handleClearByPattern}
                  isClearing={clearingRoutes.has(groupName)}
                  t={t}
                  colorStyle={getGroupColorStyle(groupName)}
                  icon={getGroupIcon(groupName)}
                />
              ));
            })()}
            
            {cacheStatus.entries.length > entriesLimit && (
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500 mb-2">
                  {t('ADMIN_CACHE', 'SHOWING_LIMITED', 'Affichage limité aux {{shown}} premières entrées ({{total}} au total)', { 
                    shown: entriesLimit,
                    total: cacheStatus.entries.length 
                  })}
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleLoadMoreEntries}
                  className="mt-1"
                >
                  <FontAwesomeIcon icon={faSync} className="mr-2" />
                  {t('ADMIN_CACHE', 'LOAD_MORE', 'Charger plus')}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FontAwesomeIcon icon={faInfoCircle} className="text-3xl mb-2" />
            <p>{t('ADMIN_CACHE', 'NO_ENTRIES', 'Aucune entrée détaillée disponible')}</p>
          </div>
        )}
      </div>

      {/* Section Cache KV */}
      <KVCacheSection />

    </div>
  );
};

export default CacheAdmin;
